/**
 * Quick stats grid: compact weekly metrics dashboard.
 * Row 1: Attendance (home groups, ministry services)
 * Row 2: Events this week, total users + growth
 * 
 * Only visible to full admins (admin.full permission).
 */

import { IconAlertTriangle } from "../../../components/Icons.tsx";
import { haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import { useAdminOverviewStats } from "./hooks";

export function QuickStatsGrid() {
  const { hasPermission } = useAuth();
  
  // Only show to full admins
  const isAdmin = hasPermission("admin.full");
  
  const { stats, isLoading, error: statsError, refetch } = useAdminOverviewStats({
    pause: !isAdmin, // Skip query if not admin
  });
  const error = statsError?.message ?? null;

  // Hide entire component for non-admins
  if (!isAdmin) {
    return null;
  }

  if (error || (!stats && !isLoading)) {
    return (
      <div className="mb-5">
        <h2 className="text-[13px] font-medium text-slate-500 mb-2">
          Статистика недели
        </h2>
        <div className="py-4 px-4 rounded-2xl bg-amber-50 border border-amber-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <IconAlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-medium text-amber-800">
                Не удалось загрузить статистику
              </p>
              <p className="text-[12px] text-amber-600 mt-0.5">
                Проверьте подключение к интернету
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              haptic("light");
              refetch();
            }}
            disabled={isLoading}
            className="mt-3 w-full py-2.5 bg-amber-500 text-white text-[13px] font-medium rounded-xl active:bg-amber-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? "Загрузка..." : "Попробовать снова"}
          </button>
        </div>
      </div>
    );
  }
  
  // Show loading state while no stats
  if (!stats) {
    return (
      <div className="mb-5">
        <h2 className="text-[13px] font-medium text-slate-500 mb-2">
          Статистика недели
        </h2>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="py-3 px-3 rounded-xl bg-purple-50 border border-purple-100">
              <div className="w-8 h-6 bg-purple-200/50 rounded animate-pulse" />
            </div>
            <div className="py-3 px-3 rounded-xl bg-blue-50 border border-blue-100">
              <div className="w-8 h-6 bg-blue-200/50 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5">
      <h2 className="text-[13px] font-medium text-slate-500 mb-2">
        Статистика недели
      </h2>

      <div className="space-y-2">
        {/* Row 1: Посещаемость */}
        <div className="grid grid-cols-2 gap-2">
          <div className="py-3 px-3 rounded-xl bg-purple-50 border border-purple-100">
            <div className="text-[10px] uppercase tracking-wide text-purple-500 mb-1">
              Посетители
            </div>
            {isLoading ? (
              <div className="w-8 h-6 bg-purple-200/50 rounded animate-pulse" />
            ) : (
              <div className="text-xl font-bold text-purple-700">
                {stats.homeGroupAttendanceThisWeek}
              </div>
            )}
            <div className="text-[11px] text-purple-600 mt-0.5">
              домашние группы
            </div>
          </div>

          <div className="py-3 px-3 rounded-xl bg-blue-50 border border-blue-100">
            <div className="text-[10px] uppercase tracking-wide text-blue-500 mb-1">
              Посетители
            </div>
            {isLoading ? (
              <div className="w-8 h-6 bg-blue-200/50 rounded animate-pulse" />
            ) : (
              <div className="text-xl font-bold text-blue-700">
                {stats.activeMinistryMembersThisWeek}
              </div>
            )}
            <div className="text-[11px] text-blue-600 mt-0.5">служения</div>
          </div>
        </div>

        {/* Row 2: События + Пользователи */}
        <div className="grid grid-cols-2 gap-2">
          <div className="py-3 px-3 rounded-xl bg-orange-50 border border-orange-100">
            <div className="text-[10px] uppercase tracking-wide text-orange-500 mb-1">
              События
            </div>
            {isLoading ? (
              <div className="w-8 h-6 bg-orange-200/50 rounded animate-pulse" />
            ) : (
              <div className="text-xl font-bold text-orange-700">
                {stats.eventsThisWeek}
              </div>
            )}
            <div className="text-[11px] text-orange-600 mt-0.5">
              запланировано
            </div>
          </div>

          <div className="py-3 px-3 rounded-xl bg-slate-100 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
              Пользователи
            </div>
            {isLoading ? (
              <div className="w-12 h-6 bg-slate-200 rounded animate-pulse" />
            ) : (
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-slate-700">
                  {stats.totalAccounts}
                </span>
                {stats.newAccountsThisWeek > 0 && (
                  <span className="text-[12px] font-semibold text-emerald-600">
                    +{stats.newAccountsThisWeek}
                  </span>
                )}
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-0.5">
              всего в приложении
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
